import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Hardware / off-flow exit page.
 *
 * Reached when MicroResearch.requestMic() detects the respondent's device
 * cannot deliver the strict 1280x720 user-facing video that a video-enabled
 * campaign requires.
 *
 * For panel-routed respondents we hit `/session/exit` so the backend
 * can reject the assignment on Connect (no payout for incompatible hardware),
 * then bounce the browser to Connect's task-complete URL so the worker isn't
 * stuck on our domain.
 */
export default function MicroResearchExit() {
  const [search] = useSearchParams();
  const reason = search.get("reason") || "hardware_required";
  const source = search.get("source") || "intercept";
  const participantId = search.get("participant_id");
  const assignmentId = search.get("assignment_id");
  const projectId = search.get("project_id");
  const interviewId = search.get("interview_id");
  const [posted, setPosted] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (posted) return;
    (async () => {
      try {
        const { data } = await api.post("/session/exit", {
          interview_id: interviewId || null,
          source,
          reason,
          participant_id: participantId,
          assignment_id: assignmentId,
          project_id: projectId,
        });
        if (data?.redirect_url) {
          setRedirecting(true);
          // Hold the screen briefly so the user sees they're being bounced.
          setTimeout(() => { window.location.href = data.redirect_url; }, 900);
        }
      } catch {
        /* best-effort — never block the off-flow screen */
      } finally {
        setPosted(true);
      }
    })();
  }, [posted, interviewId, source, reason, participantId, assignmentId, projectId]);

  const isHardware = reason === "hardware_required";

  return (
    <div
      className="min-h-screen bg-[var(--rl-bg-subtle)] flex items-center justify-center px-6 py-12"
      data-testid="micro-research-exit-screen"
    >
      <div className="text-center max-w-lg">
        {redirecting ? (
          <Loader2 className="w-12 h-12 text-[var(--rl-brand)] mx-auto animate-spin" />
        ) : (
          <AlertTriangle className="w-12 h-12 text-[var(--rl-warning,#FFB800)] mx-auto" />
        )}
        <h1 className="font-display font-black text-3xl mt-6" data-testid="exit-title">
          {redirecting
            ? "Returning you to the panel…"
            : isHardware ? "Your device isn't compatible with this study" : "Survey closed"}
        </h1>
        <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
          {redirecting
            ? "We're sending you back to Connect to release this assignment. No charge."
            : isHardware
              ? "This study requires a 720p user-facing camera. Please try again on a device with a built-in webcam (laptop or phone). No reward will be charged."
              : "Thanks for stopping by. This survey is no longer accepting respondents."}
        </p>
        <p className="text-xs text-[var(--rl-text-secondary)] mt-6" data-testid="exit-reason">
          reason: {reason} • source: {source}
        </p>
      </div>
    </div>
  );
}
